import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaChevronDown,
  FaChevronRight,
  FaSignOutAlt,
  FaChartLine
} from "react-icons/fa";
import {
  LuLayoutDashboard,
  LuCircleUser,
  LuBriefcaseBusiness,
  LuFileText,
  LuCircleHelp,
  LuCreditCard,
  LuTags,
  LuGift,
  LuVideo,
  LuHistory,
  LuTrash2,
  LuBookOpen,
  LuChevronDown,
  LuChevronUp,
  LuLock,
  LuTarget,
  LuGlobe,
  LuMonitorSmartphone
} from "react-icons/lu";
import { useTheme } from "../context/ThemeContext";
import logo from "../Assets/HD Quality Logo 2.svg";

const AdminSidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const [dashboardDropdown, setDashboardDropdown] = useState(false);
  const [recycleBinCount, setRecycleBinCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const { sidebarCollapsed, setSidebarCollapsed } = useTheme();

  useEffect(() => {
    const fetchRecycleBinCount = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/api/auth/recycle-bin/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setRecycleBinCount(data.count || 0);
        }
      } catch (err) {
        console.error("Failed to fetch recycle bin count", err);
      }
    };

    fetchRecycleBinCount();
    const interval = setInterval(fetchRecycleBinCount, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sidebarOpen, setSidebarOpen]);

  const isActive = (path) => location.pathname === path;

  // Auto-open Dashboard dropdown if on a child route
  useEffect(() => {
    if (location.pathname === '/dashboard' || location.pathname === '/contacts' || location.pathname === '/analytics') {
      setDashboardDropdown('dashboard');
    }
    if (location.pathname.startsWith('/email-campaigns')) {
      setDashboardDropdown('emails');
    }
    if (location.pathname.startsWith('/cms')) {
      setDashboardDropdown('websiteCms');
    }
  }, [location.pathname]);

  const handleLinkClick = () => {
    if (window.innerWidth <= 1024) {
      setSidebarOpen(false);
    }
  };

  const SidebarNavLabel = ({ text, active }) => {
    const containerRef = useRef(null);
    const textRef = useRef(null);
    const isLong = typeof text === 'string' && text.length > 18;
    const [isOverflowing, setIsOverflowing] = useState(isLong);

    useEffect(() => {
      const checkOverflow = () => {
        if (containerRef.current && textRef.current) {
          const hasOverflow = textRef.current.scrollWidth > containerRef.current.clientWidth + 4;
          setIsOverflowing(hasOverflow || isLong);
        }
      };
      checkOverflow();
      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }, [text, isLong]);

    return (
      <div
        ref={containerRef}
        className={`flex-1 min-w-0 relative overflow-hidden ${isOverflowing ? 'sidebar-marquee-container' : ''} ${active ? 'pr-4' : ''}`}
        title={typeof text === 'string' ? text : ''}
      >
        {isOverflowing ? (
          <div className="sidebar-marquee-track inline-flex items-center whitespace-nowrap">
            <span ref={textRef} className="font-medium shrink-0 pr-6">
              {text}
            </span>
            <span className="opacity-40 text-xs shrink-0 pr-6">•</span>
            <span className="font-medium shrink-0 pr-6">
              {text}
            </span>
            <span className="opacity-40 text-xs shrink-0 pr-6">•</span>
          </div>
        ) : (
          <span ref={textRef} className="font-medium truncate block">
            {text}
          </span>
        )}
      </div>
    );
  };

  const NavLink = ({ to, icon: Icon, children, badge = null, index = 0 }) => {
    const active = to === '/cms/pages'
      ? location.pathname.startsWith('/cms/pages') || location.pathname === '/cms'
      : isActive(to);
    return (
      <Link
        to={to}
        onClick={handleLinkClick}
        className={`sidebar-link group ${active ? 'active' : ''} opacity-0 animate-fade-in-up`}
        style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
        title={sidebarCollapsed ? children : ''}
      >
        <Icon
          className={`w-5 h-5 shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${active ? 'text-secondary-400' : 'text-white/70 group-hover:text-secondary-400'}`}
        />
        {!sidebarCollapsed && <SidebarNavLabel text={children} active={active} />}
        {badge !== null && !sidebarCollapsed && (
          <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-2 ring-primary-700 shrink-0">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
        {active && !sidebarCollapsed && (
          <div className="absolute right-3 w-2 h-2 bg-white rounded-full animate-ping-slow shrink-0" />
        )}
      </Link>
    );
  };

  return (
    <aside
      ref={sidebarRef}
      className={`
        fixed top-0 left-0 z-50 h-screen
        bg-sidebar-gradient
        dark:from-dark-950 dark:via-dark-900 dark:to-dark-950
        transform transition-all duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarCollapsed ? 'lg:w-20 w-72' : 'w-72'}
        flex flex-col
        shadow-xl lg:shadow-none
      `}
    >
      {/* Decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/10 via-transparent to-primary-500/10 pointer-events-none" />
      {/* Sidebar Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-white/10 gap-2">
        <div className={`flex-1 flex items-center ${sidebarCollapsed ? 'justify-start' : 'pr-6'}`}>
          {!sidebarCollapsed && (
            <img
              src={logo}
              alt="BooksComply"
              className="h-10 w-auto max-w-[250px] object-contain mr-10"
            />
          )}
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2 rounded-xl text-white/70 hover:bg-white/10 transition-colors"
        >
          <FaChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:block p-2 rounded-xl text-white/70 hover:bg-white/10 transition-colors"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <FaChevronRight className="w-4 h-4" />
          ) : (
            <FaChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto pt-4 pb-4 px-3 scrollbar-hide">
        <nav className="space-y-1">
          {/* Dashboard Group */}
          {sidebarCollapsed ? (
            <NavLink to="/dashboard" icon={LuLayoutDashboard} index={0}>Dashboard</NavLink>
          ) : (
            <div className="relative">
              <button
                onClick={() => setDashboardDropdown(prev => prev === 'dashboard' ? false : 'dashboard')}
                className={`
                  w-full sidebar-link justify-between
                  ${dashboardDropdown === 'dashboard' ? 'bg-white/10 text-white font-bold' : ''}
                `}
              >
                <div className="flex items-center gap-3">
                  <LuLayoutDashboard className={`w-5 h-5 ${dashboardDropdown === 'dashboard' ? 'text-secondary-400' : 'text-white/70'}`} />
                  <span className="font-medium">Dashboard</span>
                </div>
                <FaChevronDown className={`w-3 h-3 transition-transform duration-200 text-white/50 ${dashboardDropdown === 'dashboard' ? 'rotate-180' : ''}`} />
              </button>

              <div className={`
                overflow-hidden transition-all duration-300 ease-in-out
                ${dashboardDropdown === 'dashboard' ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}
              `}>
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-white/20 pl-4">
                  <Link
                    to="/dashboard"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/dashboard')
                        ? 'bg-white/20 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    Overview
                  </Link>
                  <Link
                    to="/contacts"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/contacts')
                        ? 'bg-white/20 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    Contacts Hub
                  </Link>
                  <Link
                    to="/analytics"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/analytics')
                        ? 'bg-white/20 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    Analytics
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 px-4 mb-2">
            {!sidebarCollapsed && (
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-white/40">
                Management
              </h3>
            )}
            <div className="h-px bg-gradient-to-r from-primary-500/20 to-transparent" />
          </div>

          <NavLink to="/client" icon={LuCircleUser} index={1}>Client</NavLink>
          <NavLink to="/employee" icon={LuBriefcaseBusiness} index={2}>Employee</NavLink>
          <NavLink to="/blogs" icon={LuFileText} index={3}>Blogs</NavLink>
          <NavLink to="/questions" icon={LuCircleHelp} index={4}>Questions</NavLink>
          <NavLink to="/cms/pages" icon={LuGlobe} index={5}>Website CMS</NavLink>
          <NavLink to="/portal-cms" icon={LuMonitorSmartphone} index={5.5}>Portal CMS</NavLink>

          <div className="mt-4 px-4 mb-2">
            {!sidebarCollapsed && (
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-white/40">
                Finance & Reports
              </h3>
            )}
            <div className="h-px bg-gradient-to-r from-primary-500/20 to-transparent" />
          </div>

          <NavLink to="/financial-reports" icon={FaChartLine} index={5.8}>Financial Reports</NavLink>
          <NavLink to="/payments" icon={LuCreditCard} index={6}>Payments</NavLink>
          {/* Email Campaigns Group */}
          {sidebarCollapsed ? (
            <NavLink to="/email-campaigns/scheduler" icon={LuBookOpen} index={7}>Email Campaigns</NavLink>
          ) : (
            <div className="relative">
              <button
                onClick={() => setDashboardDropdown(prev => prev === 'emails' ? false : 'emails')}
                className={`
                  w-full sidebar-link justify-between
                  ${dashboardDropdown === 'emails' ? 'bg-white/10 text-white font-bold' : ''}
                `}
              >
                <div className="flex items-center gap-3">
                  <LuBookOpen className={`w-5 h-5 ${dashboardDropdown === 'emails' ? 'text-secondary-400' : 'text-white/70'}`} />
                  <span className="font-medium">Email Campaigns</span>
                </div>
                <FaChevronDown className={`w-3 h-3 transition-transform duration-200 text-white/50 ${dashboardDropdown === 'emails' ? 'rotate-180' : ''}`} />
              </button>

              <div className={`
                overflow-hidden transition-all duration-300 ease-in-out
                ${dashboardDropdown === 'emails' ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}
              `}>
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-white/20 pl-4">
                  <Link
                    to="/email-campaigns/events"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/email-campaigns/events')
                        ? 'bg-white/20 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    Events
                  </Link>
                  <Link
                    to="/email-campaigns/templates"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/email-campaigns/templates')
                        ? 'bg-white/20 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    Templates
                  </Link>
                  <Link
                    to="/email-campaigns/scheduler"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/email-campaigns/scheduler')
                        ? 'bg-white/20 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    Scheduler
                  </Link>
                  <Link
                    to="/email-campaigns/logs"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${isActive('/email-campaigns/logs')
                        ? 'bg-white/20 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    Email Logs
                  </Link>
                </div>
              </div>
            </div>
          )}          <NavLink to="/promocodes" icon={LuTags} index={7}>Promocodes</NavLink>
          <NavLink to="/referrals" icon={LuGift} index={7}>Referrals</NavLink>
          <NavLink to="/meetings" icon={LuVideo} index={8}>Meetings</NavLink>
          <NavLink to="/login-history" icon={LuHistory} index={9}>Login History</NavLink>
        </nav>
      </div>

      {/* Footer Section - Action Card style like User Sidebar */}
      <div className="px-3 pb-6 border-t border-white/10 pt-4 bg-inherit relative z-10">
        <Link
          to="/recycle-bin"
          onClick={handleLinkClick}
          className={`group relative flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-4 py-3 rounded-xl overflow-hidden transition-all duration-300 ${isActive('/recycle-bin')
            ? 'bg-gradient-to-r from-red-500 via-orange-500 to-pink-500 text-white shadow-lg shadow-orange-500/40'
            : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
            }`}
          title={sidebarCollapsed ? 'Recycle Bin' : ''}
        >
          {/* Animated Background Shimmer */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

          {/* Icon with Animation */}
          <div className={`relative p-2 rounded-lg transition-all duration-300 ${isActive('/recycle-bin')
            ? 'bg-white/20'
            : 'bg-red-500/20 group-hover:bg-red-500'
            }`}>
            <LuTrash2 className={`w-5 h-5 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12 ${isActive('/recycle-bin') ? 'text-white' : 'text-red-500 group-hover:text-white'
              }`} />
          </div>

          {/* Text Content */}
          {!sidebarCollapsed && (
            <>
              <div className="flex flex-col">
                <span className="font-bold text-sm">Recycle Bin</span>
                <span className={`text-[10px] transition-colors ${isActive('/recycle-bin')
                  ? 'text-white/80'
                  : 'text-white/40 group-hover:text-white/60'
                  }`}>
                  Manage Deleted Items
                </span>
              </div>

              {/* Badge */}
              {recycleBinCount > 0 && (
                <span className="ml-auto bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-2 ring-primary-700 shadow-lg">
                  {recycleBinCount > 9 ? '9+' : recycleBinCount}
                </span>
              )}
            </>
          )}

          {/* Active Indicator Pulse */}
          {isActive('/recycle-bin') && !sidebarCollapsed && (
            <div className="absolute top-2 right-2 w-2 h-2 bg-white rounded-full animate-pulse" />
          )}
        </Link>
      </div>

      {/* Decorative Element */}
      <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-3xl pointer-events-none opacity-30 bg-gradient-to-br from-primary-500 to-secondary-500" />
      <div className="absolute top-1/3 -right-8 w-20 h-20 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm rotate-12 pointer-events-none animate-float opacity-40" />
    </aside>
  );
};

export default AdminSidebar;
